import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FractalCanvas } from './FractalCanvas';

export const FRACTAL_DPR_CHANGE_EVENT = 'fractal:dprchange';
export const MAX_RENDER_PIXELS = 1920 * 1500;

export function deviceScale() {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
}

export function cappedBackingSize(cssWidth, cssHeight, maxPixels = MAX_RENDER_PIXELS) {
  if (!(cssWidth > 0 && cssHeight > 0)) return null;
  const scale = Math.min(deviceScale(), Math.sqrt(maxPixels / (cssWidth * cssHeight)));
  let width = Math.max(1, Math.round(cssWidth * scale));
  let height = Math.max(1, Math.round(cssHeight * scale));
  if (width * height > maxPixels) {
    width = Math.min(maxPixels, Math.max(1, Math.floor(cssWidth * scale)));
    height = Math.min(Math.floor(maxPixels / width), Math.max(1, Math.floor(cssHeight * scale)));
  }
  return { width, height };
}

export function watchDevicePixelRatio(onChange) {
  let mediaQuery;
  const unbindMediaQuery = () => {
    if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener('change', handleDprChange);
    else mediaQuery?.removeListener?.(handleDprChange);
  };
  function handleDprChange() {
    onChange();
    bindMediaQuery();
  }
  function bindMediaQuery() {
    unbindMediaQuery();
    if (typeof window.matchMedia !== 'function') return;
    mediaQuery = window.matchMedia(`(resolution: ${deviceScale()}dppx)`);
    if (mediaQuery?.addEventListener) mediaQuery.addEventListener('change', handleDprChange);
    else mediaQuery?.addListener?.(handleDprChange);
  }
  bindMediaQuery();
  window.addEventListener(FRACTAL_DPR_CHANGE_EVENT, handleDprChange);
  return () => {
    window.removeEventListener(FRACTAL_DPR_CHANGE_EVENT, handleDprChange);
    unbindMediaQuery();
  };
}

function formatSpanLabel(camera, xRange, yRange, width, height) {
  if (camera?.spanY != null) {
    const aspect = Number.isFinite(camera.aspect) && camera.aspect > 0
      ? camera.aspect
      : (width > 0 && height > 0 ? width / height : 1);
    const num = Number(camera.spanY) * aspect;
    if (Number.isFinite(num) && num > 0) return `span ${num.toExponential(2)}`;
    const match = String(camera.spanY).trim().match(/^([+-]?(?:\d+(?:\.\d*)?|\.\d+))(?:e([+-]?\d+))?$/i);
    if (match) {
      const mantissa = Number(match[1]) * aspect;
      const exp = Number(match[2] ?? 0);
      if (Number.isFinite(mantissa) && mantissa > 0 && Number.isFinite(exp)) {
        const normExp = Math.floor(Math.log10(mantissa));
        const scaled = mantissa / (10 ** normExp);
        const totalExp = exp + normExp;
        return `span ${scaled.toFixed(2)}e${totalExp >= 0 ? `+${totalExp}` : totalExp}`;
      }
    }
    return 'Deep zoom';
  }
  if (Array.isArray(xRange) && xRange.length === 2) {
    const spanX = Math.abs(xRange[1] - xRange[0]);
    if (Number.isFinite(spanX) && spanX > 0) return `span ${spanX.toExponential(2)}`;
  }
  if (Array.isArray(yRange) && yRange.length === 2 && width > 0 && height > 0) {
    const spanX = Math.abs(yRange[1] - yRange[0]) * (width / height);
    if (Number.isFinite(spanX) && spanX > 0) return `span ${spanX.toExponential(2)}`;
  }
  return 'Deep zoom';
}

export function ResponsiveFractalCanvas({
  width: presetWidth,
  height: presetHeight,
  fullWidth = false,
  deferUntilVisible = false,
  fallbackSrc,
  fallbackAlt,
  fetchPriority,
  fetchpriority,
  ...props
}) {
  const wrapperRef = useRef(null);
  const initialRangesRef = useRef(null);
  const hasDimensionsRef = useRef(false);
  const [dimensions, setDimensions] = useState(null);
  const [visible, setVisible] = useState(
    () => !deferUntilVisible || (!globalThis.__PRERENDER__ && typeof IntersectionObserver === 'undefined')
  );
  const resolvedFetchPriority = fetchPriority ?? fetchpriority;
  const hasControls = Boolean(props.showControls || props.camera);
  const placeholderIterations = Number.isInteger(props.iterationBudget) && props.iterationBudget > 0
    ? props.iterationBudget
    : (props.camera ? 4096 : 1024);

  useEffect(() => {
    if (visible || globalThis.__PRERENDER__) return undefined;
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(item => item.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [visible]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const updateSize = () => {
      if (!wrapper) return;
      if (hasDimensionsRef.current && typeof document !== 'undefined'
          && document.body?.classList.contains('fullscreen-active')) {
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      const cssWidth = rect.width || wrapper.clientWidth;
      if (cssWidth <= 0) return;
      const cssHeight = fullWidth ? (rect.height || wrapper.clientHeight) : 0;
      let width;
      let height;
      if (fullWidth && cssHeight > 0) {
        ({ width, height } = cappedBackingSize(cssWidth, cssHeight));
      } else {
        width = Math.min(presetWidth, Math.max(1, Math.round(cssWidth * deviceScale())));
        height = Math.min(presetHeight, Math.max(1, Math.round(width * presetHeight / presetWidth)));
      }
      hasDimensionsRef.current = true;
      setDimensions(previous => previous?.width === width && previous?.height === height
        ? previous : { width, height });
    };

    updateSize();
    const stopWatchingDpr = watchDevicePixelRatio(updateSize);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateSize);
    observer?.observe(wrapper);
    window.addEventListener('resize', updateSize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateSize);
      stopWatchingDpr();
    };
  }, [presetWidth, presetHeight, fullWidth]);

  if (dimensions && !initialRangesRef.current && fullWidth && props.xRange && props.yRange) {
    const aspect = dimensions.width / dimensions.height;
    const centerX = (props.xRange[0] + props.xRange[1]) / 2;
    const centerY = (props.yRange[0] + props.yRange[1]) / 2;
    const spanY = Math.max(props.yRange[1] - props.yRange[0], (props.xRange[1] - props.xRange[0]) / aspect);
    const spanX = spanY * aspect;
    initialRangesRef.current = {
      xRange: [centerX - spanX / 2, centerX + spanX / 2],
      yRange: [centerY - spanY / 2, centerY + spanY / 2],
    };
  }

  return <div className={`responsive-fractal${fullWidth ? ' responsive-fractal-full-width' : ''}`} ref={wrapperRef}
    style={{ maxWidth: fullWidth ? undefined : presetWidth, '--fractal-aspect': `${presetWidth} / ${presetHeight}` }}>
    {visible && dimensions ? <FractalCanvas
      width={dimensions.width}
      height={dimensions.height}
      presetWidth={presetWidth}
      presetHeight={presetHeight}
      fallbackSrc={fallbackSrc}
      fallbackAlt={fallbackAlt}
      fetchPriority={resolvedFetchPriority}
      {...props}
      {...initialRangesRef.current}
    /> : (fallbackSrc || deferUntilVisible || hasControls) ? <figure>
      <div className={`image-container${hasControls ? ' deep-zoom-container' : ''}`} data-fullscreen="false">
        <div
          className="fractal-subsection-placeholder"
          style={{
            width: presetWidth,
            maxWidth: '100%',
            height: 'auto',
            aspectRatio: `${presetWidth} / ${presetHeight}`,
          }}
        >
          {fallbackSrc && <img
            loading={resolvedFetchPriority === 'high' ? undefined : 'lazy'}
            fetchPriority={resolvedFetchPriority}
            decoding="async"
            src={fallbackSrc}
            alt={fallbackAlt || 'Julia set fractal'}
            width={presetWidth}
            height={presetHeight}
          />}
        </div>
        {hasControls && <div className="deep-zoom-controls" aria-hidden="true" data-nosnippet="">
          <button type="button" disabled tabIndex={-1}>Undo</button>
          <button type="button" disabled tabIndex={-1}>Reset</button>
        </div>}
        {hasControls && <div className="deep-zoom-status" aria-hidden="true" data-nosnippet="">
          <span>{formatSpanLabel(props.camera, props.xRange, props.yRange, presetWidth, presetHeight)}</span>
          <span>{`${placeholderIterations.toLocaleString()} iter max · Ready`}</span>
        </div>}
      </div>
    </figure> : null}
  </div>;
}
